import { Link, useLocation, useNavigate } from "react-router-dom";
import { useEffect } from "react";
import {
    ShieldCheck,
    Sprout,
    Sparkles,
    BadgeCheck,
    Wallet,
} from "lucide-react";
import { LOGO_URL, BRAND } from "../lib/brand";
import { useAuth } from "../context/AuthContext";
import AuthForm from "../components/AuthForm";
import { AUTH } from "../lib/testIds";

const BENEFITS = [
    {
        icon: BadgeCheck,
        title: "Verified neighbors only",
        body: "Every member confirms an Easton Park address. No strangers, no city-wide weirdness.",
    },
    {
        icon: ShieldCheck,
        title: "Payments protected by Stripe",
        body: "Your card never touches Lendly servers — and lenders get paid only after pickup.",
    },
    {
        icon: Wallet,
        title: "90% goes to the lender",
        body: "Lendly takes a flat 10% platform fee. Earn from the gear sitting in your garage.",
    },
];

const COPY = {
    login: {
        testId: AUTH.loginPage,
        chip: "Sign in",
        eyebrow: "Welcome back",
        heading: "Sign in to Lendly",
        sub: "Pick up where you left off — your rentals, listings, and saved neighbors are waiting.",
        cardEyebrow: "Sign in",
        cardHeading: "Welcome, Lendly Neighbor.",
        cardSub: "Sign in with the email and password you used to register.",
    },
    signup: {
        testId: AUTH.signupPage,
        chip: "Join Lendly",
        eyebrow: "Become a neighbor",
        heading: "Welcome to Your Community Marketplace",
        sub: "Rent from your block. Lend to your neighbors. We'll verify your Easton Park address on the next step.",
        cardEyebrow: "Create your account",
        cardHeading: "Become a Lendly Neighbor.",
        cardSub: "Create your Lendly account with an email and password — just two fields.",
    },
};

export const AuthPage = ({ mode = "login" }) => {
    const { isAuthenticated, profileComplete, loading } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const fallback = location.state?.from || "/";
    const copy = COPY[mode];

    useEffect(() => {
        if (loading) return;
        if (isAuthenticated) {
            navigate(profileComplete ? fallback : "/signup/profile", { replace: true });
        }
    }, [isAuthenticated, profileComplete, loading, navigate, fallback]);

    return (
        <main
            data-testid={copy.testId}
            className="relative z-10 overflow-hidden px-6 py-14 md:px-10 md:py-20"
        >
            <div
                aria-hidden="true"
                className="pointer-events-none absolute -top-24 -right-16 h-[380px] w-[380px] rounded-full bg-[#737a3d]/15 blur-3xl"
            />
            <div
                aria-hidden="true"
                className="pointer-events-none absolute -bottom-24 -left-16 h-[320px] w-[320px] rounded-full bg-[#B56C5A]/10 blur-3xl"
            />

            <div className="relative mx-auto grid max-w-6xl items-center gap-10 lg:grid-cols-[1fr_1fr]">
                {/* Brand panel */}
                <section className="animate-fade-up">
                    <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-[#2e3220]/10 bg-[#F4F1EA]/70 px-3 py-1.5 backdrop-blur">
                        <Sparkles className="h-3.5 w-3.5 text-[#737a3d]" strokeWidth={2.5} />
                        <span className="text-xs font-bold uppercase tracking-widest text-[#737a3d]">
                            {copy.eyebrow}
                        </span>
                    </div>

                    <h1 className="font-display text-4xl font-bold leading-[1.05] tracking-tight text-[#2e3220] sm:text-5xl">
                        {copy.heading}
                    </h1>
                    <p className="mt-4 max-w-md text-base leading-relaxed text-[#5a6142]">
                        {copy.sub}
                    </p>

                    <div className="relative mt-8 hidden max-w-md lg:block">
                        <div className="relative aspect-[1.25/1] w-full">
                            <div className="absolute inset-0 rounded-[36px] border border-[#2e3220]/10 bg-gradient-to-br from-[#E8E2D0] to-[#d5cfb8] shadow-[0_30px_80px_rgba(46,50,32,0.12)]" />
                            <div className="absolute inset-0 flex items-center justify-center p-10">
                                <img
                                    src={LOGO_URL}
                                    alt="Lendly logo"
                                    className="h-full w-full object-contain drop-shadow-[0_6px_24px_rgba(46,50,32,0.15)]"
                                />
                            </div>
                        </div>
                    </div>

                    <ul className="mt-8 hidden space-y-3 lg:block">
                        {BENEFITS.map((b) => {
                            const Icon = b.icon;
                            return (
                                <li
                                    key={b.title}
                                    className="flex items-start gap-3 rounded-2xl border border-[#2e3220]/10 bg-[#F4F1EA] p-4"
                                >
                                    <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-[#737a3d]/12 text-[#737a3d]">
                                        <Icon className="h-4 w-4" strokeWidth={2.25} />
                                    </span>
                                    <div className="min-w-0">
                                        <p className="font-display text-sm font-bold text-[#2e3220]">
                                            {b.title}
                                        </p>
                                        <p className="mt-0.5 text-xs leading-relaxed text-[#5a6142]">
                                            {b.body}
                                        </p>
                                    </div>
                                </li>
                            );
                        })}
                    </ul>
                </section>

                {/* Form panel */}
                <section className="animate-fade-up [animation-delay:120ms]">
                    <div className="rounded-3xl border border-[#2e3220]/10 bg-[#F4F1EA] p-7 shadow-[0_20px_60px_rgba(46,50,32,0.10)] md:p-9">
                        <Link
                            to="/"
                            className="mb-5 inline-flex items-center gap-2 lg:hidden"
                            aria-label={`${BRAND.name} home`}
                        >
                            <img
                                src={LOGO_URL}
                                alt="Lendly"
                                className="h-10 w-10 rounded-xl object-cover"
                            />
                            <span className="font-display text-2xl font-bold text-[#2e3220]">
                                {BRAND.name}
                            </span>
                        </Link>

                        <p className="text-xs font-bold uppercase tracking-widest text-[#737a3d]">
                            {copy.cardEyebrow}
                        </p>
                        <h2 className="mt-1 font-display text-3xl font-bold leading-tight text-[#2e3220]">
                            {copy.cardHeading}
                        </h2>
                        <p className="mt-2 text-sm leading-relaxed text-[#5a6142]">
                            {copy.cardSub}
                        </p>

                        <div className="mt-7">
                            <AuthForm mode={mode} />
                        </div>

                        <div className="mt-6 rounded-2xl border border-[#737a3d]/15 bg-[#737a3d]/5 px-4 py-3 text-left">
                            <p className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest text-[#737a3d]">
                                <Sprout className="h-3.5 w-3.5" />
                                Verified neighborhood
                            </p>
                            <p className="mt-1 text-xs leading-relaxed text-[#5a6142]">
                                Lendly is currently invite-only for Easton Park (ZIP 78744).
                                You&apos;ll confirm your address on the next step. Outside the
                                neighborhood? We&apos;ll add you to the waitlist.
                            </p>
                        </div>

                        <p className="mt-5 text-center text-xs text-[#5a6142]">
                            By continuing, you agree to our{" "}
                            <Link
                                to="/terms"
                                className="font-semibold text-[#737a3d] hover:underline"
                            >
                                Community Terms
                            </Link>
                            .
                        </p>
                    </div>
                </section>
            </div>
        </main>
    );
};

export default AuthPage;
